import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "motion/react";
import { useRef, useState, useEffect } from "react";
import { useAuthModal } from "../context/AuthModalContext";

// ─── Typewriter ───────────────────────────────────────────
const phrases = [
  "Chartered Accountants",
  "CA Firms",
  "Tax Professionals",
  "Tax Consultants",
];

function useTypewriter() {
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    const phrase = phrases[currentPhrase];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (charIndex < phrase.length) {
          setDisplayText(phrase.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (charIndex > 0) {
          setDisplayText(phrase.slice(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        } else {
          setIsDeleting(false);
          setCurrentPhrase((currentPhrase + 1) % phrases.length);
        }
      }
    }, isDeleting ? 50 : 80);
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, currentPhrase]);

  return displayText;
}

// ─── Counter animation ────────────────────────────────────
function StatItem({ number, suffix, label }: { number: string; suffix?: string; label: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div ref={ref} style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5 }}
        style={{ fontSize: "32px", fontWeight: 800, color: "#C9A84C", lineHeight: 1 }}
      >
        {number}{suffix}
      </motion.div>
      <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", marginTop: "6px" }}>{label}</div>
    </div>
  );
}

// ─── Tools data ───────────────────────────────────────────
const TOOLS = [
  {
    icon: "📈", name: "Capital Gain Calculator", tag: "paid", link: "/tools/capital-gain",
    desc: "Process broker statements from Zerodha, Groww, AngelOne and generate a complete ITR-ready capital gain report.",
  },
  {
    icon: "🧮", name: "TDS Calculator", tag: "free", link: "/tools/tds-calculator",
    desc: "Calculate TDS for any payment under all Sections — 194C, 194J, 194H and more. Get instant rate lookups.",
  },
  {
    icon: "🏦", name: "Bank Statement Converter", tag: "paid", link: "/tools/bank-statement",
    desc: "Convert PDF bank statements from HDFC, SBI, ICICI and 50+ banks into a structured, reconciled Excel report.",
  },
  {
    icon: "🏠", name: "Property Gain Calculator", tag: "free", link: "/tools/property-gain",
    desc: "Calculate capital gain on property sale with indexation, improvement cost, and Section 54 / 54F exemptions.",
  },
  {
    icon: "🌏", name: "Overseas TDS Calculator", tag: "paid", link: "/tools/overseas-tds",
    desc: "Calculate TDS on foreign remittances under Section 206AA and DTAA provisions for any country.",
  },
  {
    icon: "🪙", name: "Crypto Gain Calculator", tag: "comingSoon", link: "/tools/crypto-gain",
    desc: "Track crypto transactions, calculate 30% VDA tax, and generate Schedule VDA for your ITR. Coming soon.",
  },
];

const TAG_LABELS: Record<string, string> = { free: "Free", paid: "Paid", comingSoon: "Coming Soon" };
const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  free:       { bg: "rgba(46,125,50,0.2)",    color: "#4CAF50" },
  paid:       { bg: "rgba(201,168,76,0.2)",   color: "#C9A84C" },
  comingSoon: { bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)" },
};

// Staggered float durations per card (Change 1)
const CARD_FLOAT_ANIMS = [
  "float-card 3.5s ease-in-out infinite",
  "float-card 4.0s ease-in-out 0.5s infinite",
  "float-card 3.8s ease-in-out 1.0s infinite",
  "float-card 4.2s ease-in-out 0.3s infinite",
  "float-card 3.6s ease-in-out 0.8s infinite",
  "float-card 4.1s ease-in-out 1.2s infinite",
];

// ─── Bank strips ──────────────────────────────────────────
const BANKS_ROW1 = ["SBI", "HDFC Bank", "ICICI Bank", "Axis Bank", "Kotak Bank", "Punjab National Bank", "Bank of Baroda", "Yes Bank", "IndusInd Bank", "Canara Bank"];
const BANKS_ROW2 = ["Union Bank", "Bank of India", "Central Bank", "DCB Bank", "Indian Bank", "Indian Overseas Bank", "Zerodha", "Groww", "AngelOne", "Mutual Funds"];

function BankPill({ name }: { name: string }) {
  return (
    <span style={{
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      padding: "8px 16px", borderRadius: "20px",
      fontSize: "13px", color: "rgba(255,255,255,0.7)",
      margin: "0 8px", whiteSpace: "nowrap",
    }}>
      {name}
    </span>
  );
}

// ─── Video showcase data (Change 3) ──────────────────────
const VIDEO_CARDS = [
  {
    icon: "📈", tool: "Capital Gain Calculator", duration: "3:12",
    desc: "Generate a complete 5-sheet capital gain report from your Zerodha Tax P&L in under 3 minutes",
    gradient: "linear-gradient(135deg, #1A1A1A 0%, #2C2418 100%)",
  },
  {
    icon: "🏦", tool: "Bank Statement to Excel", duration: "2:45",
    desc: "Convert any SBI or HDFC bank statement PDF to Excel with AI narration in under 2 minutes",
    gradient: "linear-gradient(135deg, #1A1A1A 0%, #181824 100%)",
  },
];

// ─── Main component ───────────────────────────────────────
export default function Home() {
  const { openModal } = useAuthModal();
  const navigate = useNavigate();
  const typewriterText = useTypewriter();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoNotifyEmail, setVideoNotifyEmail] = useState("");

  const handleToolClick = (tool: typeof TOOLS[0], e: React.MouseEvent) => {
    if (tool.tag === "comingSoon") { e.preventDefault(); return; }
    if (tool.tag === "paid") { e.preventDefault(); navigate("/pricing"); return; }
  };

  const handleVideoNotify = () => {
    if (videoNotifyEmail.trim()) {
      const stored: string[] = JSON.parse(localStorage.getItem("video_notify_emails") || "[]");
      if (!stored.includes(videoNotifyEmail.trim())) {
        stored.push(videoNotifyEmail.trim());
        localStorage.setItem("video_notify_emails", JSON.stringify(stored));
      }
      setVideoNotifyEmail("");
      setVideoModalOpen(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#0A0A0A" }}>

      {/* ══════════════════════════════════════════════
          SECTION A — HERO
      ══════════════════════════════════════════════ */}
      <div style={{
        minHeight: "100vh", position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center", background: "#0A0A0A",
      }}>
        <div className="dot-grid" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }} />
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          background: "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(201,168,76,0.08) 0%, transparent 70%)",
          pointerEvents: "none", zIndex: 1,
        }} />

        <div className="hero-inner" style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: "1200px", margin: "0 auto", padding: "80px 32px 60px", display: "flex", alignItems: "center", gap: "40px" }}>

          {/* LEFT COLUMN: Floating tool grid (55%) — Change 1 */}
          <div className="hero-tools-col" style={{ flex: "0 0 55%", minWidth: 0 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {TOOLS.map((tool, idx) => {
                const tagColor = TAG_COLORS[tool.tag];
                const isComingSoon = tool.tag === "comingSoon";
                const isHovered = hoveredCard === idx;
                return (
                  <Link
                    key={idx}
                    to={tool.link}
                    onClick={e => handleToolClick(tool, e)}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${isHovered && !isComingSoon ? "rgba(201,168,76,0.4)" : "rgba(255,255,255,0.08)"}`,
                      borderRadius: "12px",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      textDecoration: "none",
                      cursor: isComingSoon ? "default" : "pointer",
                      opacity: isComingSoon ? 0.5 : 1,
                      position: "relative",
                      animation: isHovered ? "none" : CARD_FLOAT_ANIMS[idx],
                      transform: isHovered && !isComingSoon ? "translateY(-4px) scale(1.02)" : undefined,
                      boxShadow: isHovered && !isComingSoon
                        ? "0 16px 40px rgba(201,168,76,0.25)"
                        : "0 4px 16px rgba(0,0,0,0.3)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                    }}
                  >
                    <div style={{ fontSize: "28px", marginBottom: "6px" }}>{tool.icon}</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "white", marginBottom: "4px", lineHeight: 1.3 }}>
                      {tool.name}
                    </div>
                    <div style={{
                      fontSize: "11px", color: "rgba(255,255,255,0.5)",
                      lineHeight: 1.4, marginBottom: "8px", flex: 1,
                      display: "-webkit-box", WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical" as const, overflow: "hidden",
                    }}>
                      {tool.desc}
                    </div>
                    <span style={{
                      fontSize: "10px", fontWeight: 700,
                      padding: "2px 8px", borderRadius: "8px",
                      background: tagColor.bg, color: tagColor.color,
                      alignSelf: "flex-start",
                    }}>
                      {TAG_LABELS[tool.tag]}
                    </span>
                    {/* Hover arrow — only clickable cards */}
                    {!isComingSoon && (
                      <span style={{
                        position: "absolute", right: "12px", bottom: "12px",
                        fontSize: "14px", color: "#C9A84C",
                        opacity: isHovered ? 1 : 0,
                        transition: "opacity 0.2s",
                      }}>→</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* RIGHT COLUMN: Marketing copy (45%) — Change 1: floating doc cards removed */}
          <div className="hero-copy-col" style={{ flex: "0 0 45%", minWidth: 0 }}>
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ display: "inline-block", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.4)", borderRadius: "20px", padding: "5px 16px", fontSize: "13px", color: "#C9A84C", fontWeight: 600, marginBottom: "14px" }}
            >
              Built by a Chartered Accountant
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 style={{
                fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 800,
                letterSpacing: "-0.04em", lineHeight: 1.05, marginBottom: "4px",
                background: "linear-gradient(135deg, #FFFFFF 0%, #E8D5A0 50%, #C9A84C 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
              }}>
                Smart Tax Tools for
              </h1>
              <div style={{
                fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 800,
                letterSpacing: "-0.04em", lineHeight: 1.05, marginBottom: "14px",
                background: "linear-gradient(135deg, #FFFFFF 0%, #E8D5A0 50%, #C9A84C 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                minHeight: "1.2em",
              }}>
                {typewriterText}
                <span style={{
                  display: "inline-block", width: "3px", height: "0.85em",
                  background: "#C9A84C", marginLeft: "3px", verticalAlign: "text-bottom",
                  animation: "blink 1s step-end infinite", WebkitTextFillColor: "#C9A84C",
                }} />
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ fontSize: "15px", color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginBottom: "20px" }}
            >
              Purpose-built tools for Indian tax practice. Save hours every ITR season.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="hero-buttons"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}
            >
              <button className="btn-gold-premium" style={{ fontSize: "14px", padding: "12px 24px" }} onClick={openModal}>
                Get Started Free →
              </button>
              <button className="btn-ghost-premium" style={{ fontSize: "14px", padding: "12px 24px" }}
                onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}>
                See How It Works
              </button>
            </motion.div>

            {/* Trust badges — Change 2: India flag uses CSS circle, not emoji */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "center" }}
            >
              {["🔒 Secure", "📊 CA-verified", "⚡ Instant"].map(text => (
                <span key={text} style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center", gap: "4px" }}>{text}</span>
              ))}
              {/* India flag as CSS tricolour circle — avoids "IN" rendering on some systems */}
              <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                <span style={{
                  width: "14px", height: "14px", borderRadius: "50%", flexShrink: 0, display: "inline-block",
                  background: "linear-gradient(180deg, #FF9933 33%, white 33%, white 66%, #138808 66%)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }} />
                Built for India
              </span>
            </motion.div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION B — STATS BAR
      ══════════════════════════════════════════════ */}
      <div style={{
        background: "rgba(255,255,255,0.03)",
        borderTop: "1px solid rgba(201,168,76,0.1)",
        borderBottom: "1px solid rgba(201,168,76,0.1)",
        backdropFilter: "blur(20px)",
        padding: "32px",
      }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: "0" }}>
          {[
            { number: "6", suffix: "+", label: "CA Tools Available" },
            { number: "100", suffix: "%", label: "Tax Law Accurate" },
            { number: "2", suffix: " min", label: "Average Report Time" },
            { number: "Free", suffix: "", label: "To Get Started" },
          ].map((stat, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", flex: 1 }}>
              <StatItem number={stat.number} suffix={stat.suffix} label={stat.label} />
              {i < 3 && <div style={{ width: "1px", height: "48px", background: "rgba(255,255,255,0.08)", flexShrink: 0 }} />}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION C — VIDEO SHOWCASE  (Change 3)
          Replaces the old "Everything a CA needs" tab section
      ══════════════════════════════════════════════ */}
      <div style={{ background: "#050505", padding: "80px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ fontSize: "36px", fontWeight: 800, textAlign: "center", color: "white", marginBottom: "12px" }}
          >
            See TaxThinker in action
          </motion.h2>
          <p style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", marginBottom: "48px", fontSize: "16px" }}>
            Watch how our top tools save CAs hours of work
          </p>

          {/* TODO: Replace placeholder cards with actual video embeds when recordings are ready */}
          <div className="video-cards-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "28px" }}>
            {VIDEO_CARDS.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                onClick={() => setVideoModalOpen(true)}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "16px",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = "rgba(201,168,76,0.4)";
                  el.style.transform = "translateY(-3px)";
                  el.style.boxShadow = "0 12px 40px rgba(0,0,0,0.4)";
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = "rgba(255,255,255,0.1)";
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "none";
                }}
              >
                {/* Video thumbnail (16:9 ratio) */}
                <div style={{ position: "relative", paddingBottom: "56.25%", background: card.gradient }}>
                  {/* Play button */}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div
                      style={{
                        width: "64px", height: "64px", borderRadius: "50%",
                        background: "rgba(201,168,76,0.9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "22px", cursor: "pointer",
                        boxShadow: "0 4px 20px rgba(201,168,76,0.4)",
                        transition: "transform 0.2s",
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "scale(1.1)"; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
                    >▶</div>
                  </div>
                  {/* Tool overlay — bottom left */}
                  <div style={{ position: "absolute", bottom: "16px", left: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "24px" }}>{card.icon}</span>
                    <span style={{ color: "white", fontWeight: 700, fontSize: "15px", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{card.tool}</span>
                  </div>
                  {/* Duration badge — top right */}
                  <div style={{ position: "absolute", top: "12px", right: "12px", background: "rgba(0,0,0,0.7)", color: "rgba(255,255,255,0.9)", fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "6px" }}>
                    {card.duration}
                  </div>
                </div>
                {/* Info panel */}
                <div style={{ padding: "20px" }}>
                  <h3 style={{ fontSize: "16px", fontWeight: 700, color: "white", marginBottom: "8px" }}>{card.tool}</h3>
                  <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)", lineHeight: 1.6, marginBottom: "12px" }}>{card.desc}</p>
                  <span style={{ fontSize: "13px", color: "#C9A84C", fontWeight: 600 }}>Watch now →</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Section CTA */}
          <div style={{ textAlign: "center", marginTop: "48px" }}>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "16px", marginBottom: "16px" }}>
              Ready to try it yourself?
            </p>
            <button className="btn-gold-premium" style={{ fontSize: "15px", padding: "13px 32px" }} onClick={openModal}>
              Start for free →
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION D — HOW IT WORKS
      ══════════════════════════════════════════════ */}
      <div id="how-it-works" style={{ background: "#050505", padding: "80px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ fontSize: "36px", fontWeight: 800, textAlign: "center", color: "white", marginBottom: "12px" }}
          >
            From PDF to Excel in minutes
          </motion.h2>
          <p style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", marginBottom: "56px", fontSize: "16px" }}>
            No manual entry. No spreadsheet formulas. Just upload and download.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }} className="how-steps-grid">
            {[
              { step: "01", icon: "☁️", title: "Upload your PDF", desc: "Drag & drop any bank statement, broker report or PDF statement. We accept PDF, Excel and CSV." },
              { step: "02", icon: "⚡", title: "AI processes it", desc: "Our AI extracts every transaction accurately, applies the right tax rules, and structures the data." },
              { step: "03", icon: "📊", title: "Download Excel", desc: "Get a professional multi-sheet report instantly — formatted exactly for ITR filing and CA audit use." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px", padding: "32px 24px" }}
              >
                <div style={{ fontSize: "48px", fontWeight: 800, marginBottom: "16px", background: "linear-gradient(135deg, #E8D5A0 0%, #C9A84C 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                  {item.step}
                </div>
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>{item.icon}</div>
                <h3 style={{ fontSize: "18px", fontWeight: 700, color: "white", marginBottom: "10px" }}>{item.title}</h3>
                <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION E — BANK LOGOS MARQUEE
      ══════════════════════════════════════════════ */}
      <div style={{ background: "#0A0A0A", padding: "64px 0", overflow: "hidden" }}>
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "28px" }}>
          Works with all major Indian banks
        </p>
        <div style={{ overflow: "hidden", marginBottom: "12px" }}>
          <div className="scroll-left">
            {[...BANKS_ROW1, ...BANKS_ROW1].map((b, i) => <BankPill key={i} name={b} />)}
          </div>
        </div>
        <div style={{ overflow: "hidden" }}>
          <div className="scroll-right">
            {[...BANKS_ROW2, ...BANKS_ROW2].map((b, i) => <BankPill key={i} name={b} />)}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION F — TESTIMONIALS
      ══════════════════════════════════════════════ */}
      <div style={{ background: "#050505", padding: "80px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ fontSize: "36px", fontWeight: 800, textAlign: "center", color: "white", marginBottom: "48px" }}
          >
            Trusted by CAs across India
          </motion.h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }} className="testimonials-grid">
            {[
              { text: "Saves me 3-4 hours per client during ITR season. The Capital Gain report format is exactly what my clients need.", name: "CA Priya Sharma", city: "Mumbai", initials: "PS" },
              { text: "The bank statement converter handles HDFC and SBI perfectly. The AI narration feature is brilliant.", name: "CA Rajan Mehta", city: "Delhi", initials: "RM" },
              { text: "Finally a tool built specifically for Indian CAs. The Section 54 exemption calculator is incredibly detailed.", name: "CA Sunita Agarwal", city: "Bangalore", initials: "SA" },
            ].map((t, i) => (
              <motion.div
                key={i}
                className="testimonial-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div style={{ fontSize: "36px", color: "#C9A84C", lineHeight: 1, marginBottom: "12px", fontFamily: "Georgia, serif" }}>"</div>
                <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)", lineHeight: 1.8, marginBottom: "20px" }}>{t.text}</p>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "13px", color: "#0A0A0A", flexShrink: 0 }}>
                    {t.initials}
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>{t.name}</div>
                    <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.4)" }}>{t.city}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          SECTION G — PRICING TEASER
      ══════════════════════════════════════════════ */}
      <div style={{ background: "#0A0A0A", padding: "80px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }} className="pricing-teaser-grid">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{ fontSize: "40px", fontWeight: 800, color: "white", lineHeight: 1.1, marginBottom: "20px" }}>
              Start free.<br />
              <span style={{ background: "linear-gradient(135deg, #E8D5A0, #C9A84C)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Scale as you grow.</span>
            </h2>
            <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.5)", lineHeight: 1.8, marginBottom: "16px" }}>
              No hidden fees. Pay only when you generate reports. Free tools stay free forever.
            </p>
            <Link to="/pricing"
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#C9A84C", fontWeight: 700, fontSize: "15px", textDecoration: "none" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}>
              View all plans →
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              background: "linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(201,168,76,0.02) 100%)",
              border: "1px solid rgba(201,168,76,0.3)",
              borderRadius: "20px", padding: "36px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#C9A84C", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "12px" }}>CA Plan</div>
            <div style={{ fontSize: "42px", fontWeight: 800, color: "white", marginBottom: "4px" }}>₹999<span style={{ fontSize: "16px", fontWeight: 400, color: "rgba(255,255,255,0.4)" }}>/month</span></div>
            <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.35)", marginBottom: "24px" }}>Billed monthly. Cancel anytime.</div>
            {["Unlimited bank statement conversions", "Capital gain reports (Zerodha, Groww, AngelOne)", "Overseas TDS calculator", "Priority email support", "All future tools included"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "14px", color: "rgba(255,255,255,0.7)", marginBottom: "10px" }}>
                <span style={{ color: "#C9A84C", fontWeight: 700, flexShrink: 0 }}>✓</span> {f}
              </div>
            ))}
            <button
              onClick={openModal}
              style={{ width: "100%", marginTop: "20px", background: "#C9A84C", color: "#0A0A0A", padding: "14px", borderRadius: "10px", fontWeight: 800, fontSize: "15px", border: "none", cursor: "pointer", transition: "transform 0.2s" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "scale(1.02)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = "scale(1)"; }}
            >
              Start 7-day free trial
            </button>
          </motion.div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          VIDEO "COMING SOON" MODAL  (Change 3)
      ══════════════════════════════════════════════ */}
      <AnimatePresence>
        {videoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideoModalOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
              zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
              padding: "24px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: "#1A1A1A",
                border: "1px solid rgba(201,168,76,0.3)",
                borderRadius: "20px",
                padding: "40px 36px",
                maxWidth: "440px", width: "100%",
                boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px", textAlign: "center" }}>🎬</div>
              <h2 style={{
                fontSize: "22px", fontWeight: 800, textAlign: "center", marginBottom: "12px",
                background: "linear-gradient(135deg, #FFFFFF 0%, #C9A84C 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
              }}>
                Video coming soon!
              </h2>
              <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.55)", textAlign: "center", lineHeight: 1.7, marginBottom: "24px" }}>
                We are recording walkthroughs of all our tools. Subscribe below to be notified when videos go live.
              </p>
              <input
                type="email"
                value={videoNotifyEmail}
                onChange={e => setVideoNotifyEmail(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleVideoNotify(); }}
                placeholder="your@email.com"
                style={{
                  width: "100%", padding: "11px 14px", marginBottom: "12px",
                  background: "rgba(255,255,255,0.06)", border: "1.5px solid rgba(255,255,255,0.12)",
                  borderRadius: "8px", fontSize: "14px", color: "white",
                  outline: "none", boxSizing: "border-box",
                }}
                onFocus={e => { e.target.style.borderColor = "#C9A84C"; }}
                onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.12)"; }}
              />
              <button
                onClick={handleVideoNotify}
                style={{ width: "100%", padding: "12px", background: "#C9A84C", color: "#0A0A0A", border: "none", borderRadius: "8px", fontWeight: 800, fontSize: "14px", cursor: "pointer", marginBottom: "8px" }}
              >
                Notify Me
              </button>
              <button
                onClick={() => setVideoModalOpen(false)}
                style={{ width: "100%", padding: "10px", background: "transparent", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", fontSize: "13px", cursor: "pointer" }}
              >
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
